<script setup lang="ts">
import { computed, provide } from 'vue'

import { useProvideCarousel } from './use-carousel'
import { classMerge } from '../utils'

import type { CarouselEmits, CarouselProps, WithClassAsProps } from './carousel-interface'

const props = withDefaults(defineProps<CarouselProps & WithClassAsProps>(), {
  loop: false,
  orientation: 'horizontal',
  align: 'center',
  variant: 'simple'
})

const emits = defineEmits<CarouselEmits>()

const { canScrollNext, canScrollPrev, carouselApi, carouselRef, loop, orientation, scrollNext, scrollPrev } = useProvideCarousel(props, emits)

defineExpose({
  canScrollNext,
  canScrollPrev,
  carouselApi,
  carouselRef,
  loop,
  orientation,
  scrollNext,
  scrollPrev
})

function onKeyDown (event: KeyboardEvent) {
  const prevKey = props.orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft'
  const nextKey = props.orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight'

  if (event.key === prevKey) {
    event.preventDefault()
    scrollPrev()

    return
  }

  if (event.key === nextKey) {
    event.preventDefault()
    scrollNext()
  }
}

const computedVariant = computed(() => props.variant)

provide('variant', computedVariant)

</script>

<template>
  <div :class="classMerge('relative outline-none focus-visible:outline-none focus-visible:ring focus-visible:ring-offset-1 ring-ring', props.class)"
       role="region"
       aria-roledescription="carousel"
       tabindex="0"
       @keydown="onKeyDown">
    <slot :can-scroll-next="canScrollNext"
          :can-scroll-prev="canScrollPrev"
          :carousel-api="carouselApi"
          :carousel-ref="carouselRef"
          :loop="loop"
          :orientation="orientation"
          :scroll-next="scrollNext"
          :scroll-prev="scrollPrev" />
  </div>
</template>
